"use client";
import useTranslate from "@app/_hooks/useTranslate";
import {
  ChevronDown as ArrowDownIcon,
  ChevronUp as ArrowUpIcon,
} from "lucide-react";

import { cn } from "@/lib/utils";
import type { VariantSelectorProps } from "@/utils/hooks/useVariantSelector";
import useVariantSelector from "@/utils/hooks/useVariantSelector";
import Swatch from "@app/_components/VariantSelector/Swatch";
import { useMarket } from "@app/_context";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";

import { formatPrice } from "@/utils/priceUtils";
import isMatch from "lodash.ismatch";
import Image from "next/image";
import type {
  SanitizedVariant,
  VariantState,
  VariantStateAttribute,
  VariantStateOption,
} from "types/variant";
import { StockStatusIcon } from "../StockStatusIcon";

const selectableStockStatuses: number[] = [
  1, // In Stock
  2, // Incoming
  3, // Pending Sold Out
  4, // Delivery Item
  5, // Out of Stock
  6, // Sold Out
];

function sortAttributeOptions(
  attribute: VariantStateAttribute,
  state: VariantState,
  prevKeys: string[] = [],
): (VariantStateOption & SanitizedVariant)[] {
  const { options, selection } = state;
  return [...attribute.options.values()]
    .map((value) => {
      const option = options.get(attribute.key);
      const variants = option?.get(value.value);
      const matchingVariants = findMatchingVariants(
        variants,
        selection,
        prevKeys,
      );
      const [variant, ...rest] = matchingVariants
        .sort((a, b) => a.sellingPrice - b.sellingPrice)
        .sort((a, b) => a.stock - b.stock);
      return { ...value, ...variant, fromPrice: rest.length > 0 };
    })
    .sort((a, b) => String(a.value).localeCompare(String(b.value)))
    .sort((a, b) => a.sellingPrice - b.sellingPrice)
    .sort((a, b) => a.stock - b.stock);
}

function findMatchingVariants(
  variants: any[] = [],
  selection: any,
  prevKeys: string[] = [],
) {
  if (!prevKeys.length) return variants;
  const curatedSelection = Object.fromEntries(
    prevKeys.map((key) => [key, selection[key]]),
  );
  return variants.filter((variant) => isMatch(variant, curatedSelection));
}

/**
 * @todo Refactor using shadcn/ui and the Select component from Radux UI.
 * It's also too much logic and nesting in this component with no clear separation of concerns.
 */
const VariantSelector = ({ className, ...props }: VariantSelectorProps) => {
  const t = useTranslate();
  const { attributes } = props.state;
  const { state, onChange } = useVariantSelector(props);
  /**
   * Read the selection from the reducer state, not from props: the server
   * only re-renders once a complete variant has been pushed to the URL, so
   * props lag behind while the user is mid-selection (e.g. after switching
   * to a finish that clears the seat option).
   */
  const { selection } = state;
  const market = useMarket().state.market;

  if (!attributes.length) return null;

  return (
    <div
      className={cn("relative flex flex-col gap-4", className)}
      data-testid="ProductVariantSelect"
    >
      {attributes.map((attribute, index, arr) => {
        const { key, label } = attribute;
        const prevKeys = arr.slice(0, index).map((a) => a.key);
        const prevKey = prevKeys.at(-1);
        const option = attribute.options.get(String(selection[key]));
        const options = sortAttributeOptions(attribute, state, prevKeys);

        return (
          <Listbox
            data-testid="ProductVariantList"
            key={key}
            as="div"
            className="relative"
            disabled={typeof prevKey === "string" && !selection[prevKey]}
            value={state.selection[key] ?? null}
            name={key}
            onChange={(value) => onChange(key, value)}
          >
            {({ open }) => (
              <>
                <label className="relative mb-2 block text-sm">{label}</label>
                <ListboxButton
                  name={key}
                  className={cn(
                    "ui-disabled:border-gray-150 flex h-10 w-full items-center border border-black py-0 pr-4 text-left text-base transition lg:h-12",
                    !state.selection[key] && "text-gray-300",
                  )}
                >
                  <div className="mr-auto flex items-center self-stretch truncate">
                    {state.selection[key] ? (
                      <>
                        {option?.swatch && (
                          <Image
                            className="w-10 lg:w-12"
                            src={option.swatch}
                            alt={String(state.selection[key])}
                            width={48}
                            height={48}
                          />
                        )}
                        <span className="ml-4 font-normal">
                          {state.selection[key]}
                        </span>
                      </>
                    ) : (
                      <span className="ml-4">
                        {t("chooseAttribute", { label: label.toLowerCase() })}
                      </span>
                    )}
                  </div>
                  {open ? (
                    <ArrowUpIcon
                      strokeWidth={1}
                      className="size-4.5 text-black"
                    />
                  ) : (
                    <ArrowDownIcon
                      strokeWidth={1}
                      className="size-4.5 text-black"
                    />
                  )}
                </ListboxButton>
                <ListboxOptions
                  as="ul"
                  anchor="bottom"
                  className="bg-body hr-y absolute right-0 left-0 z-50 max-h-96 w-[var(--button-width)] overflow-x-hidden overflow-y-auto border border-t-0 border-black"
                >
                  {options.map((option) => {
                    if (!option.key) return null;
                    return (
                      <ListboxOption
                        as="li"
                        key={option.key}
                        value={option.value}
                        disabled={
                          !selectableStockStatuses.includes(option.stock)
                        }
                        className="ui-active:bg-gray-100 ui-disabled:pointer-events-none ui-disabled:text-black/40 flex h-12 cursor-pointer items-center px-3 py-2.5 max-md:text-sm"
                      >
                        {option.swatch && (
                          <Swatch
                            name={option.value}
                            imgUrl={option.swatch ?? ""}
                          />
                        )}
                        <span>{option.value}</span>
                        <span className="ml-6 flex-1 space-y-1 text-right text-sm whitespace-nowrap uppercase">
                          <span className="ml-auto flex items-center justify-end gap-x-1">
                            {[1, 3].includes(option.stock) &&
                              option.stockNumber > 0 && (
                                <>
                                  <StockStatusIcon />
                                  {t("inStock")}
                                </>
                              )}
                            {option.stock === 2 && (
                              <>
                                <StockStatusIcon />
                                {t("inStockSoon")}
                              </>
                            )}
                          </span>
                        </span>
                        <div className="ml-6 flex shrink-0 flex-col-reverse space-y-px text-right text-base">
                          <span>
                            {option.fromPrice && t("fromShortened") + " "}
                            <span className="font-normal">
                              {formatPrice(option.sellingPrice, market.locale)}
                            </span>
                          </span>
                          {option.listPrice > option.sellingPrice && (
                            <span className="ml-2 text-sm text-gray-300 line-through">
                              {formatPrice(
                                option.listPrice ?? 0,
                                market.locale,
                              )}
                            </span>
                          )}
                        </div>
                      </ListboxOption>
                    );
                  })}
                </ListboxOptions>
              </>
            )}
          </Listbox>
        );
      })}
    </div>
  );
};

VariantSelector.displayName = "VariantSelector";

export default VariantSelector;
